import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { EDITORIAL_IMAGES } from "@/lib/site";
import { lineMaskReveal, fadeUp, EASE_OUT_EXPO } from "@/lib/motion";

/**
 * KineticHero
 * Signature on-load moment for LotWise Auto.
 *
 * Craft notes:
 * - Split headline into masked lines. Each line reveals from clip-path
 *   underneath its own line-mask, in sequence. This is the one moment.
 * - One editorial product photograph, treated as a clipped, spotlighted
 *   frame on the right, with subtle parallax on scroll.
 * - Grain overlay + slow-drifting gradient bloom for depth.
 * - No cluttered widgets. The hero commits to one gesture: authority.
 */
export default function KineticHero() {
  const containerRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  const imageY = useTransform(
    scrollYProgress,
    [0, 1],
    prefersReducedMotion ? [0, 0] : [0, 80]
  );
  const imageScale = useTransform(
    scrollYProgress,
    [0, 1],
    prefersReducedMotion ? [1, 1] : [1, 1.08]
  );
  const bloomY = useTransform(
    scrollYProgress,
    [0, 1],
    prefersReducedMotion ? [0, 0] : [0, 140]
  );

  const line1 = ["Dealership", "inventory,"];
  const line2 = ["consistently"];
  const line3 = ["market", "ready."];

  return (
    <section
      ref={containerRef}
      data-testid="hero-section"
      className="relative isolate overflow-hidden bg-[#050609] text-white"
    >
      {/* Grain */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.9'/></svg>\")",
        }}
      />

      {/* Bloom */}
      <motion.div
        aria-hidden
        style={{ y: bloomY }}
        className="pointer-events-none absolute -top-40 right-[-10%] h-[820px] w-[820px] rounded-full opacity-70"
      >
        <div
          className="h-full w-full rounded-full"
          style={{
            background:
              "radial-gradient(closest-side, rgba(79,13,255,0.5), rgba(10,181,255,0.16) 40%, transparent 72%)",
            filter: "blur(60px)",
          }}
        />
      </motion.div>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_rgba(10,181,255,0.18),_transparent_50%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent"
      />

      {/* Top meta bar */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: EASE_OUT_EXPO, delay: 0.05 }}
        className="container-lw relative flex items-center justify-between pt-10 text-[10px] font-semibold uppercase tracking-[0.28em] text-white/50"
      >
        <span data-testid="hero-meta-index">Ch. 00 / Overture</span>
        <span className="hidden md:inline">Southeastern Ontario</span>
        <span>Est. 2025</span>
      </motion.div>

      <div className="container-lw relative grid gap-16 pb-24 pt-14 md:pb-32 md:pt-20 lg:grid-cols-[1.15fr_1fr] lg:gap-14 lg:pb-40 xl:gap-24">
        {/* Headline block */}
        <div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="eyebrow text-[#0AB5FF]"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#0AB5FF]" />
            Dealership Inventory Operations
          </motion.div>

          <h1
            className="mt-8 h-display text-[46px] leading-[0.94] sm:text-[60px] md:text-[74px] lg:text-[88px] xl:text-[104px]"
            data-testid="hero-headline"
          >
            <MaskedLine words={line1} row={0} />
            <MaskedLine words={line2} row={1} accent />
            <MaskedLine words={line3} row={2} accent />
          </h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={4}
            className="mt-8 max-w-xl text-[17px] leading-relaxed text-white/70 md:text-lg"
          >
            LotWise Auto is a dedicated dealership inventory operation.
            Recurring onsite visits handle photos, vehicle details,
            listing builds, and cross-platform publishing so every unit
            on your lot is market ready across every priority channel.
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={5}
            className="mt-10 flex flex-wrap items-center gap-3"
          >
            <Link
              to="/request-a-visit"
              data-testid="hero-request-visit-cta"
              className="btn-primary-lw text-base"
            >
              Get My Dealership Plan
              <ArrowUpRight className="h-4 w-4" />
            </Link>
            <a
              href="#dealer-qualifier"
              data-testid="hero-qualifier-cta"
              className="btn-ghost-lw-dark text-base"
            >
              Calculate My Inventory Need
            </a>
          </motion.div>

          {/* Numeric proof grid */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={6}
            className="mt-14 grid max-w-xl grid-cols-3 gap-6 border-t border-white/10 pt-8"
          >
            <NumericProof k="2" l="Operators onsite" />
            <NumericProof k="5+" l="Priority platforms" />
            <NumericProof k="1" l="Dealership standard" />
          </motion.div>
        </div>

        {/* Editorial product photography — clipped, spotlighted */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.35, ease: EASE_OUT_EXPO }}
          className="relative"
        >
          <motion.div
            style={{ y: imageY, scale: imageScale }}
            className="relative aspect-[4/5] w-full overflow-hidden rounded-[6px] border border-white/10 bg-black"
          >
            <img
              src="/generated/hero/honda-crv-exl.png"
              alt="Used vehicle merchandised to the LotWise dealership standard on a Canadian dealer lot"
              className="absolute inset-0 h-full w-full object-cover object-center"
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
            {/* Spotlight */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "radial-gradient(60% 50% at 55% 45%, transparent 20%, rgba(0,0,0,0.6) 100%)",
              }}
            />
            {/* Cross overlay */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/40"
            />

            {/* Editorial caption */}
            <div className="absolute left-5 top-5 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-white/75">
              <span className="h-px w-8 bg-[#0AB5FF]" />
              Plate 01 · Onsite operation
            </div>
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-white">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-white/70">
                  Vehicle in service
                </div>
                <div className="mt-1 font-display text-lg font-semibold tracking-tight md:text-xl">
                  Merchandised to the LotWise standard
                </div>
              </div>
              <div className="text-right text-[10px] font-semibold uppercase tracking-[0.28em] text-white/60">
                LWA — 0001
              </div>
            </div>
          </motion.div>

          {/* Corner tick marks */}
          <span
            aria-hidden
            className="absolute -left-2 -top-2 h-4 w-4 border-l border-t border-white/40"
          />
          <span
            aria-hidden
            className="absolute -right-2 -top-2 h-4 w-4 border-r border-t border-white/40"
          />
          <span
            aria-hidden
            className="absolute -bottom-2 -left-2 h-4 w-4 border-b border-l border-white/40"
          />
          <span
            aria-hidden
            className="absolute -bottom-2 -right-2 h-4 w-4 border-b border-r border-white/40"
          />
        </motion.div>
      </div>
    </section>
  );
}

function MaskedLine({ words, row, accent }) {
  return (
    <span className="block overflow-hidden pb-[0.05em]">
      <motion.span
        variants={lineMaskReveal}
        initial="hidden"
        animate="show"
        custom={row}
        className="inline-block"
      >
        {words.map((w, i) => (
          <React.Fragment key={`${row}-${i}`}>
            {accent && i === words.length - 1 ? (
              <span className="text-gradient-lw">{w}</span>
            ) : (
              w
            )}
            {i !== words.length - 1 ? " " : ""}
          </React.Fragment>
        ))}
      </motion.span>
    </span>
  );
}

function NumericProof({ k, l }) {
  return (
    <div>
      <div className="font-display text-3xl font-bold tracking-tight text-white md:text-4xl">
        {k}
      </div>
      <div className="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-white/50">
        {l}
      </div>
    </div>
  );
}
